import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Features } from '@ultrasnip/contracts';
import { type FormEvent, useState } from 'react';
import { AuthLayout } from '@/components/shell/auth-layout';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { FormAlert, TextField } from '@/features/auth/form-parts';
import { authClient, authErrorMessage } from '@/lib/auth-client';

export const Route = createFileRoute('/_auth/forgot-password')({
  // Needs outgoing email, which is switched off (see Features.email).
  beforeLoad: () => {
    if (!Features.email) throw redirect({ to: '/login' });
  },
  component: ForgotPasswordScreen,
});

function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!email.includes('@')) {
      setError('Enter the email you signed up with.');
      return;
    }
    setPending(true);
    const { error: requestError } = await authClient.requestPasswordReset({
      email: email.trim(),
      redirectTo: '/reset-password',
    });
    setPending(false);
    if (requestError) {
      setError(authErrorMessage(requestError));
      return;
    }
    // Same message whether or not the account exists (no user enumeration).
    setSent(true);
  };

  return (
    <AuthLayout
      title="Reset your password"
      footer={
        <Link to="/login" className="link">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <p className="text-foreground" role="status">
          If an account exists for <span className="font-medium break-all">{email.trim()}</span>, a reset link
          is on its way.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormAlert message={error} />
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" disabled={pending} className="mt-1 h-9 w-full">
            {pending && <Spinner />}
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
